import { useCallback } from 'react';
import { bridge, getNumberRange, isNotNil } from 'shared/src/utils';
import { useQueryClient } from '@tanstack/react-query';
import { queryKey } from '../../../constant/queryKey';
import { type MarketDetail, marketService } from 'shared/src/api';

const useLogMarketAnalyticsEvent = (marketId: number) => {
  const queryClient = useQueryClient();

  return useCallback<
    (params: {
      name: string;
      channel?: Array<
        | 'firebase'
        | 'amplitude'
        | 'braze'
        | 'ablylog'
        | 'facebook'
        | 'airbridge'
      >;
      analytics?: Record<string, unknown>;
    }) => Promise<boolean>
  >(
    async params => {
      const cachedMarketDetail = queryClient.getQueryData<MarketDetail>(
        queryKey.marketDetail(marketId),
      );

      const marketDetail =
        cachedMarketDetail ??
        (await queryClient.fetchQuery({
          queryKey: queryKey.marketDetail(marketId),
          queryFn: () => marketService.getMarketDetail(marketId),
        }));

      if (!marketDetail) return false;

      const isExistDownloadableCoupon = () => {
        if (!marketDetail.market.is_exist_usable_coupon) return false;

        if (isNotNil(marketDetail.max_price_coupon)) return true;
        return false;
      };

      const isTodayMarket = () => {
        return marketDetail.market.approved_delivery_types.filter(
          item => item.value === 3,
        );
      };

      bridge.logAnalyticsEvent({
        name: params.name,
        channel: params.channel ?? [
          'firebase',
          'facebook',
          'amplitude',
          'braze',
          'ablylog',
        ],
        analytics: {
          SCREEN: 'MARKET',
          MARKET_NAME: marketDetail.market.name,
          MARKET_SNO: marketDetail.market.sno.toString(),
          FAVORITE_COUNT: marketDetail.market.favorites_count,
          FAVORITE_COUNT_RANGE: getNumberRange({
            rangeUnit: 500,
            target: marketDetail.market.favorites_count,
          }),
          REVIEW_RATING: Math.floor(
            marketDetail.market.purchasing_satisfaction_ratio,
          ),
          MARKET_TYPE: marketDetail.market.market_type.name,
          AGE_TAG1: marketDetail.market.age_tags[0],
          AGE_TAG2: marketDetail.market.age_tags[1],
          STYLE_TAG1: marketDetail.market.style_tags[0],
          STYLE_TAG2: marketDetail.market.style_tags[1],
          IS_EXIST_USABLE_COUPONS: marketDetail.market.is_exist_usable_coupon
            ? 1
            : 0,
          IS_EXIST_DOWNLOADABLE_COUPONS: isExistDownloadableCoupon() ? 1 : 0,
          IS_TODAY_MARKET: isTodayMarket().length > 0 ? 1 : 0,
          ...params.analytics,
        },
      });

      return true;
    },
    [marketId, queryClient],
  );
};

export const useLogClickFavoriteMarket = (marketId: number) => {
  const logMarketAnalytics = useLogMarketAnalyticsEvent(marketId);

  return useCallback<(isUnFavorite: boolean) => Promise<void>>(
    async isUnFavorite => {
      await logMarketAnalytics({
        name: isUnFavorite
          ? 'CLICK_UNFAVORITE_MARKET'
          : 'CLICK_FAVORITE_MARKET',
      });
    },
    [logMarketAnalytics],
  );
};
